<template>
    <teleport
        :disabled="!appendToBody"
        to="body"
    >
        <transition
            enter-active-class="transition ease-in-out duration-200"
            enter-from-class="opacity-0 translate-y-4"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition ease-in-out duration-200"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-4"
        >
            <div
                v-if="showModal"
                class="h-modal fixed left-0 right-0 top-0 flex w-full items-center justify-center overflow-y-auto overflow-x-hidden legacy-md:inset-0 legacy-md:h-full"
                :class="{
                    ...modalPositionClasses,
                    'h-full bg-white': fullscreen,
                    'h-full bg-white legacy-md:bg-transparent': fullscreenOnMobile,
                    [modalClass]: true,
                }"
                role="dialog"
                :style="{ zIndex: zIndex }"
                tabindex="-1"
            >
                <div
                    v-click-outside="clickOutsideHandler"
                    class="relative h-full w-full"
                    :class="{ ['legacy-md:h-auto ' + width]: !fullscreen }"
                >
                    <!-- Modal content -->
                    <div
                        class="relative"
                        :class="{
                            'rounded-lg bg-white shadow': !fullscreen,
                            'rounded-none bg-white shadow-none legacy-md:rounded-lg legacy-md:shadow': fullscreenOnMobile,
                            [wrapperClass]: true,
                        }"
                    >
                        <!-- Modal header -->
                        <div
                            v-if="!disableHeader"
                            :class="
                                mergeTailwindClasses(
                                    'flex items-center justify-between gap-3 rounded-t border-b p-4',
                                    headerClass,
                                )
                            "
                        >
                            <div
                                v-if="type"
                                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                                :class="{
                                    'bg-ds-red-100': type == 'danger',
                                    'bg-ds-green-100': type == 'success',
                                    'bg-ds-primary-100': type == 'info',
                                }"
                            >
                                <svg
                                    v-if="type == 'danger'"
                                    class="h-4 w-4 shrink-0 fill-current text-ds-red-500"
                                    viewBox="0 0 16 16"
                                >
                                    <path
                                        d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z"
                                    />
                                </svg>
                                <svg
                                    v-else-if="type == 'success'"
                                    class="h-4 w-4 shrink-0 fill-current text-ds-green-500"
                                    viewBox="0 0 16 16"
                                >
                                    <path
                                        d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zM7 11.4L3.6 8 5 6.6l2 2 4-4L12.4 6 7 11.4z"
                                    />
                                </svg>
                                <svg
                                    v-else-if="type == 'info'"
                                    class="h-4 w-4 shrink-0 fill-current text-content-primary"
                                    viewBox="0 0 16 16"
                                >
                                    <path
                                        d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm1 12H7V7h2v5zM8 6c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1z"
                                    />
                                </svg>
                            </div>

                            <h3 class="text-xl font-semibold text-content-primary">
                                <template v-if="$slots['title']">
                                    <slot name="title" />
                                </template>
                                <template v-else>
                                    {{ title }}
                                </template>
                            </h3>
                            <button
                                v-if="showClose"
                                class="ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-ds-primary-400 shadow-none hover:bg-ds-primary-200 hover:text-content-primary"
                                data-modal-toggle="defaultModal"
                                type="button"
                                @click="closeHandler"
                            >
                                <CloseIcon @click="userClosedHandler" />
                            </button>
                        </div>
                        <!-- Modal body -->
                        <div
                            v-if="$slots['default']"
                            :class="mergeTailwindClasses('space-y-6 p-6', bodyClass)"
                        >
                            <slot name="default" />
                        </div>
                        <!-- Modal footer -->
                        <div
                            class="flex w-full items-center space-x-2 rounded-b border-t border-ds-primary-200 p-6"
                            :class="footerClass"
                        >
                            <slot name="footer" />
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-out duration-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="showModal"
                class="fixed inset-0 bg-ds-primary-900 bg-opacity-50"
                :style="{ zIndex: zIndex - 10 }"
            ></div>
        </transition>
    </teleport>
</template>

<script lang="ts">
import CloseIcon from '@/components/Icons/CloseIcon.vue';
import { isFunction } from 'lodash';
import { twMerge, type ClassNameValue } from 'tailwind-merge';
import { defineComponent } from 'vue';

const Modal = defineComponent({
    components: { CloseIcon },
    emits: ['update:visible', 'open'],
    props: {
        type: String,
        visible: Boolean,
        title: String,
        appendToBody: {
            type: Boolean,
            default: false,
        },
        fullscreen: {
            type: Boolean,
            default: false,
        },
        fullscreenOnMobile: {
            type: Boolean,
            default: false,
        },
        top: {
            type: [String, Number],
            default: 0,
        },
        width: {
            type: String,
            default: 'max-w-2xl',
        },
        disableHeader: {
            type: Boolean,
            default: false,
        },
        disableClose: {
            type: Boolean,
            default: false,
        },
        escToClose: {
            type: Boolean,
            default: true,
        },
        showClose: {
            type: Boolean,
            default: true,
        },
        lockScroll: {
            type: Boolean,
            default: false,
        },
        modalClass: {
            type: String,
            default: 'p-4',
        },
        wrapperClass: {
            type: String,
            default: '',
        },
        headerClass: {
            type: String,
        },
        bodyClass: {
            type: String,
        },
        footerClass: {
            type: String,
        },
        zIndex: {
            type: Number,
            default: 50,
        },
        disableCloseWhenClickOutside: {
            type: Boolean,
            default: false,
        },
        beforeClose: Function,
        eventToCloseFunction: Function,
    },
    mounted() {
        this.triggerModal();
        document.addEventListener('keydown', this.closeOnEsc);
    },
    unmounted() {
        document.removeEventListener('keydown', this.closeOnEsc);
    },
    data() {
        return {
            showModal: this.visible,
        };
    },
    methods: {
        triggerModal(visible?: boolean) {
            let isVisible = visible ?? this.showModal;
            return isVisible ? this.openHandler() : this.closeHandler();
        },
        async openHandler() {
            this.$emit('open', this.closeHandler);
            this.showModal = true;
            this.lockScroll && document.body.classList.add('overflow-y-hidden');
        },
        closeOnEsc(event: KeyboardEvent) {
            if (this.visible && event.key === 'Escape' && this.escToClose) {
                if (this.eventToCloseFunction) {
                    this.eventToCloseFunction();
                }
                this.closeHandler();
            }
        },
        clickOutsideHandler() {
            if (this.eventToCloseFunction) {
                this.eventToCloseFunction();
            }
            if (!this.disableCloseWhenClickOutside) {
                this.closeHandler();
            }
        },
        userClosedHandler() {
            if (isFunction(this.eventToCloseFunction)) {
                this.eventToCloseFunction();
            }
        },
        closeHandler() {
            if (isFunction(this.beforeClose)) {
                this.beforeClose();
            }

            this.showModal = false;
            this.$emit('update:visible', false);
            this.lockScroll && document.body.classList.remove('overflow-y-hidden');
        },
        mergeTailwindClasses(...classes: ClassNameValue[]) {
            return twMerge(...classes);
        },
    },
    computed: {
        modalPositionClasses() {
            switch (parseInt(`${this.top}`)) {
                case 0:
                    return { ['top-[0%]']: true, ['translate-y-[-0%]']: true };
                case 25:
                    return { ['top-[25%]']: true, ['translate-y-[-25%]']: true };
                case 50:
                    return { ['top-[50%]']: true, ['translate-y-[-50%]']: true };
                case 75:
                    return { ['top-[75%]']: true, ['translate-y-[-75%]']: true };
                case 100:
                    return { ['top-[100%]']: true, ['translate-y-[-100%]']: true };
            }
            return {};
        },
        bodyClassMerged() {
            return twMerge('p-6 space-y-6', this.bodyClass);
        },
    },
    watch: {
        visible(newVisible) {
            this.triggerModal(newVisible);
        },
    },
});
export default Modal;
</script>
